<template>
  <div class="fuc">

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept=".mp3,.mp4,.m4a,.wav,.ogg,.flac"
      style="display:none"
      @change="onFileSelect"
    />

    <!-- Header -->
    <div class="fuc-header">
      <div class="fuc-icon">
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="9" y="2" width="10" height="14" rx="5" fill="#dce8ff" stroke="#335cff" stroke-width="1.4"/>
          <path d="M5 13.5C5 18.194 9.029 22 14 22C18.971 22 23 18.194 23 13.5" stroke="#335cff" stroke-width="1.4" stroke-linecap="round"/>
          <line x1="14" y1="22" x2="14" y2="26" stroke="#335cff" stroke-width="1.4" stroke-linecap="round"/>
          <line x1="10" y1="26" x2="18" y2="26" stroke="#335cff" stroke-width="1.4" stroke-linecap="round"/>
        </svg>
      </div>
      <div class="fuc-header-text">
        <h2 class="fuc-title">Sube el audio de tu reunión</h2>
        <p class="fuc-subtitle">La IA genera el acta por ti — sin escuchar ni transcribir</p>
      </div>
    </div>

    <!-- Drop zone -->
    <div
      class="fuc-drop"
      :class="{ 'fuc-drop--over': isDragging, 'fuc-drop--has-file': files.length }"
      @dragover.prevent="isDragging = true"
      @dragleave.prevent="isDragging = false"
      @drop.prevent="onDrop"
      @click="$refs.fileInput.click()"
    >
      <div class="fuc-drop-texts">
        <p class="fuc-drop-label">Arrastra tu grabación aquí</p>
        <p class="fuc-drop-hint">MP3, MP4, WAV y M4A · hasta 500 MB</p>
      </div>
      <button class="fuc-upload-btn" @click.stop="$refs.fileInput.click()">Elegir archivo</button>
    </div>

    <!-- File list -->
    <ul v-if="files.length" class="fuc-files">
      <li v-for="(file, i) in files" :key="i" class="fuc-file">
        <div class="fuc-thumb" v-html="file.thumb"></div>
        <div class="fuc-file-info">
          <div class="fuc-file-nameline">
            <span class="fuc-name">{{ file.name }}<span class="fuc-ext">.{{ file.ext }}</span></span>
            <span class="fuc-badge" v-if="i === 0">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 2L9.5 5.8L13.5 6.2L10.7 8.8L11.5 12.8L8 10.8L4.5 12.8L5.3 8.8L2.5 6.2L6.5 5.8Z" fill="#7d52f4"/>
              </svg>
              Primary
            </span>
          </div>
          <span class="fuc-size">{{ file.size }}</span>
        </div>
        <button class="fuc-more-btn" @click.stop="removeFile(i)" title="Eliminar">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M5 5l10 10M15 5L5 15" stroke="#a3a3a3" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>
      </li>
    </ul>

    <!-- Transcript panel -->
    <div v-if="status !== 'idle'" class="fuc-result">

      <!-- Transcribing state -->
      <div v-if="status === 'transcribing'" class="fuc-result-header">
        <span class="fuc-result-dot fuc-result-dot--live"></span>
        <span class="fuc-result-label">Transcribiendo{{ audioDuration ? ` · ${formatDuration(audioDuration)}` : '' }}</span>
      </div>

      <!-- Done state -->
      <div v-else-if="status === 'done'" class="fuc-result-header">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <circle cx="7" cy="7" r="6" fill="#0ea87a" fill-opacity="0.12"/>
          <path d="M4 7l2 2 4-4" stroke="#0ea87a" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span class="fuc-result-label fuc-result-label--done">Acta lista</span>
        <button class="fuc-copy-btn" @click="copyTranscript" :class="{ 'fuc-copy-btn--copied': copied }">
          {{ copied ? 'Copiado' : 'Copiar' }}
        </button>
      </div>

      <!-- Error state -->
      <div v-else-if="status === 'error'" class="fuc-result-header">
        <span class="fuc-result-label fuc-result-label--error">{{ errorMessage }}</span>
      </div>

      <!-- Transcript text -->
      <div v-if="transcript" class="fuc-transcript" ref="transcriptEl">
        <p v-for="(seg, i) in segments" :key="i" class="fuc-seg">{{ seg.text }}</p>
      </div>

    </div>

    <!-- CTA -->
    <button
      class="fuc-cta"
      :class="{ 'fuc-cta--loading': status === 'transcribing', 'fuc-cta--done': status === 'done' }"
      :disabled="!files.length || status === 'transcribing'"
      @click="status === 'done' ? reset() : generate()"
    >
      <svg v-if="status === 'transcribing'" class="fuc-spinner" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.3)" stroke-width="2"/>
        <path d="M8 2a6 6 0 016 6" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>
      {{ ctaLabel }}
    </button>

  </div>
</template>

<script>
const BACKEND = 'http://localhost:8000';

function randomWave(color, bg) {
  const heights = Array.from({ length: 6 }, () => 6 + Math.floor(Math.random() * 18));
  const bars = heights.map((h, i) =>
    `<rect x="${7 + i * 5}" y="${20 - h / 2}" width="3" height="${h}" rx="1.5" fill="${color}"/>`
  ).join('');
  return `<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="40" height="40" rx="6" fill="${bg}"/>
    ${bars}
  </svg>`;
}

const EXT_STYLE = {
  mp3:  { color: '#335cff', bg: '#eef3ff' },
  m4a:  { color: '#7d52f4', bg: '#f3f0ff' },
  wav:  { color: '#0ea87a', bg: '#edfaf5' },
  mp4:  { color: '#f59e0b', bg: '#fffbeb' },
  ogg:  { color: '#ef4444', bg: '#fef2f2' },
  flac: { color: '#6b7280', bg: '#f9fafb' },
};

function formatSize(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default {
  name: 'FileUploadCard',

  data() {
    return {
      isDragging: false,
      files: [],
      status: 'idle',        // idle | transcribing | done | error
      segments: [],
      audioDuration: null,
      errorMessage: '',
      copied: false,
    };
  },

  computed: {
    transcript() {
      return this.segments.map(s => s.text).join(' ');
    },
    ctaLabel() {
      const map = { idle: 'Generar acta con IA', transcribing: 'Transcribiendo…', done: 'Nueva transcripción', error: 'Reintentar' };
      return map[this.status] || 'Generar acta con IA';
    },
  },

  methods: {
    onFileSelect(e) {
      const f = e.target.files[0];
      if (f) this.addFile(f);
      e.target.value = '';
    },

    onDrop(e) {
      this.isDragging = false;
      const f = e.dataTransfer.files[0];
      if (f) this.addFile(f);
    },

    addFile(f) {
      const ext = f.name.split('.').pop().toLowerCase();
      const style = EXT_STYLE[ext] || { color: '#6b7280', bg: '#f9fafb' };
      this.files.unshift({
        name: f.name.replace(/\.[^.]+$/, ''),
        ext,
        size: formatSize(f.size),
        thumb: randomWave(style.color, style.bg),
        fileObject: f,
      });
      // reset previous result
      this.reset(false);
    },

    removeFile(i) {
      this.files.splice(i, 1);
      if (!this.files.length) this.reset(false);
    },

    async generate() {
      const file = this.files[0]?.fileObject;
      if (!file) return;

      this.status = 'transcribing';
      this.segments = [];
      this.audioDuration = null;
      this.errorMessage = '';

      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${BACKEND}/transcribe`, { method: 'POST', body: formData });

        if (!res.ok) {
          const err = await res.json().catch(() => ({ detail: res.statusText }));
          throw new Error(err.detail || 'Error del servidor');
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        let chunk = await reader.read();
        while (!chunk.done) {
          buffer += decoder.decode(chunk.value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const msg = JSON.parse(line.slice(6));

            if (msg.type === 'start') {
              this.audioDuration = msg.duration;
            } else if (msg.type === 'segment') {
              this.segments.push(msg);
              this.$nextTick(() => {
                if (this.$refs.transcriptEl) {
                  this.$refs.transcriptEl.scrollTop = this.$refs.transcriptEl.scrollHeight;
                }
              });
            } else if (msg.type === 'done') {
              this.status = 'done';
            } else if (msg.type === 'error') {
              throw new Error(msg.message);
            }
          }

          chunk = await reader.read();
        }
      } catch (err) {
        this.status = 'error';
        this.errorMessage = err.message.includes('fetch')
          ? 'No se pudo conectar al backend. ¿Está corriendo en :8000?'
          : err.message;
      }
    },

    reset(clearFiles = true) {
      this.status = 'idle';
      this.segments = [];
      this.audioDuration = null;
      this.errorMessage = '';
      this.copied = false;
      if (clearFiles) this.files = [];
    },

    copyTranscript() {
      const text = this.transcript;
      const finish = () => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      };
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(finish);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
        finish();
      }
    },

    formatDuration(secs) {
      const m = Math.floor(secs / 60);
      const s = Math.round(secs % 60);
      return m > 0 ? `${m}m ${s}s` : `${s}s`;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.fuc {
  width: 100%;
  max-width: 414px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ── HEADER ── */
.fuc-header { display: flex; flex-direction: column; gap: 24px; }
.fuc-icon { width: 28px; height: 28px; flex-shrink: 0; }
.fuc-header-text { display: flex; flex-direction: column; gap: 8px; }
.fuc-title  { font-size: 24px; font-weight: 500; line-height: 32px; color: #171717; }
.fuc-subtitle { font-size: 16px; font-weight: 400; line-height: 24px; color: #5c5c5c; letter-spacing: -0.176px; }

/* ── DROP ZONE ── */
.fuc-drop {
  background: #fff;
  border: 1px dashed #d1d1d1;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: flex-start;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.fuc-drop:hover        { border-color: #a8bfff; background: #fafbff; }
.fuc-drop--over        { border-color: #335cff; background: #f5f8ff; }
.fuc-drop--has-file    { border-style: solid; border-color: #d1d1d1; }

.fuc-drop-texts { display: flex; flex-direction: column; gap: 4px; pointer-events: none; }
.fuc-drop-label { font-size: 16px; font-weight: 500; line-height: 24px; color: #5c5c5c; letter-spacing: -0.176px; }
.fuc-drop-hint  { font-size: 14px; font-weight: 500; line-height: 20px; color: #a3a3a3; letter-spacing: -0.084px; }

.fuc-upload-btn {
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 8px;
  box-shadow: 0px 1px 2px 0px rgba(10, 13, 20, 0.03);
  padding: 6px 12px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #5c5c5c;
  letter-spacing: -0.084px;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s;
}
.fuc-upload-btn:hover { background: #fafafa; border-color: #d1d1d1; }

/* ── FILE LIST ── */
.fuc-files { list-style: none; display: flex; flex-direction: column; gap: 20px; }
.fuc-file  { display: flex; align-items: center; gap: 16px; }

.fuc-thumb {
  background: #f7f7f7;
  border-radius: 8px;
  padding: 4px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
}

.fuc-file-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 4px; }
.fuc-file-nameline { display: flex; align-items: center; gap: 8px; min-width: 0; }

.fuc-name {
  font-size: 16px; font-weight: 500; line-height: 24px;
  color: #5c5c5c; letter-spacing: -0.176px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex-shrink: 1;
}
.fuc-ext   { color: #a3a3a3; }
.fuc-size  { font-size: 14px; font-weight: 400; line-height: 20px; color: #a3a3a3; letter-spacing: -0.084px; }

.fuc-badge {
  display: inline-flex; align-items: center; gap: 2px;
  background: #efebff; color: #7d52f4;
  border-radius: 999px; padding: 2px 8px 2px 4px;
  font-size: 12px; font-weight: 500; line-height: 16px;
  white-space: nowrap; flex-shrink: 0;
}

.fuc-more-btn {
  background: transparent; border: none; border-radius: 8px;
  padding: 4px; cursor: pointer; display: flex;
  align-items: center; justify-content: center; flex-shrink: 0;
  transition: background 0.12s;
}
.fuc-more-btn:hover { background: #f5f5f5; }

/* ── RESULT PANEL ── */
.fuc-result {
  border: 1px solid #ebebeb;
  border-radius: 12px;
  overflow: hidden;
}

.fuc-result-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #fafafa;
  border-bottom: 1px solid #ebebeb;
}

.fuc-result-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
}
.fuc-result-dot--live {
  background: #335cff;
  animation: pulse 1.2s ease-in-out infinite;
}
@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.5; transform: scale(0.85); }
}

.fuc-result-label       { font-size: 12px; font-weight: 500; color: #5c5c5c; flex: 1; }
.fuc-result-label--done { color: #0ea87a; }
.fuc-result-label--error{ color: #ef4444; flex: 1; }

.fuc-copy-btn {
  font-family: inherit; font-size: 11px; font-weight: 500;
  color: #335cff; background: none; border: 1px solid #d1d1d1;
  border-radius: 6px; padding: 3px 8px; cursor: pointer;
  transition: background 0.1s;
}
.fuc-copy-btn:hover           { background: #f0f4ff; }
.fuc-copy-btn--copied         { color: #0ea87a; border-color: #0ea87a; }

.fuc-transcript {
  max-height: 200px;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  scroll-behavior: smooth;
}

.fuc-seg {
  font-size: 14px;
  line-height: 1.65;
  color: #3d3d3d;
  letter-spacing: -0.084px;
}

/* ── CTA ── */
.fuc-cta {
  width: 100%; background: #335cff; border: none; border-radius: 10px;
  padding: 10px; font-family: inherit; font-size: 14px; font-weight: 500;
  line-height: 20px; color: #fff; letter-spacing: -0.084px;
  cursor: pointer; text-align: center;
  display: flex; align-items: center; justify-content: center; gap: 8px;
  transition: background 0.15s, transform 0.1s;
}
.fuc-cta:hover:not(:disabled)  { background: #2449e0; }
.fuc-cta:active:not(:disabled) { transform: scale(0.99); }
.fuc-cta:disabled              { opacity: 0.5; cursor: not-allowed; }
.fuc-cta--done                 { background: #171717; }
.fuc-cta--done:hover           { background: #2d2d2d; }
.fuc-cta--loading              { background: #335cff; }

/* Spinner */
.fuc-spinner { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
